// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/organizations/controllers.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/organizations/src/controllers.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo stockpilot-app
//     node fleet/fleet.ts check --repo stockpilot-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { fromPromise } from "xstate";
import { invitationMachine } from "@fleet/state-machines";

export interface InvitationActions {
  acceptInvitation: (id: string) => Promise<unknown>;
  rejectInvitation: (id: string) => Promise<unknown>;
  onRefresh: () => void;
}

export function getInvitationMachineLogic(actions: InvitationActions) {
  const { acceptInvitation, rejectInvitation, onRefresh } = actions;

  return invitationMachine.provide({
    actors: {
      acceptActor: fromPromise(async ({ input }: { input: { id: string } }) => {
        await acceptInvitation(input.id);
        onRefresh();
      }),
      rejectActor: fromPromise(async ({ input }: { input: { id: string } }) => {
        await rejectInvitation(input.id);
        onRefresh();
      }),
    },
  });
}

export interface MemberManagementActions<Role = string> {
  updateMemberRole?: (memberIdOrUserId: string, role: Role) => Promise<void>;
  updateRole?: (memberIdOrUserId: string, role: Role) => Promise<void>;
  removeMember: (memberIdOrUserId: string) => Promise<void>;
  inviteMember: (email: string, role: Role) => Promise<void>;
}

export function createMemberHandlers<Role = string>(actions: MemberManagementActions<Role>) {
  const updateFn = actions.updateMemberRole ?? actions.updateRole;
  return {
    handleRoleChange: async (memberIdOrUserId: string, role: Role) => {
      if (updateFn) {
        await updateFn(memberIdOrUserId, role);
      }
    },
    handleUpdateMemberRole: async (memberIdOrUserId: string, role: Role) => {
      if (updateFn) {
        await updateFn(memberIdOrUserId, role);
      }
    },
    handleRemoveMember: async (memberIdOrUserId: string) => {
      await actions.removeMember(memberIdOrUserId);
    },
    handleInviteMember: async (email: string, role: Role) => {
      const sanitizedEmail = email.trim().toLowerCase();
      await actions.inviteMember(sanitizedEmail, role);
    },
  };
}

export function createOrganizationMembersHandlers<Role = string>(
  actions: MemberManagementActions<Role>,
) {
  return createMemberHandlers<Role>(actions);
}

/**
 * Helper to filter out an invitation from a list after it has been accepted or rejected.
 * Used for optimistic updates in the UI.
 */
export function filterInvitationsAfterAction<T extends { id: string }>(
  invitations: T[] | undefined,
  idToRemove: string,
): T[] | undefined {
  if (!invitations) {
    return undefined;
  }
  return invitations.filter((inv) => inv.id !== idToRemove);
}
